import React, { useEffect, useState } from 'react'
import { useTranslation } from 'next-i18next'
import Link from 'next/link'
import Modal from '../Modal'
import { HeroIcons } from '../../lib/utils/heroIcons'
import routes from '../../config/routes'
import { isPromoActive, PROMO_PROPERTY_ID } from '../../lib/utils/promo'

const STORAGE_KEY = 'yield_boost_popup_hidden_v1'

export default function PromoPopup() {
  const { t } = useTranslation('common')
  const [isOpen, setIsOpen] = useState(false)
  const [dontShowAgain, setDontShowAgain] = useState(false)
  const isPromo = isPromoActive()

  useEffect(() => {
    if (!isPromo) return
    if (typeof window === 'undefined') return
    try {
      const hidden = window.localStorage.getItem(STORAGE_KEY) === '1'
      if (!hidden) setIsOpen(true)
    } catch {
      setIsOpen(true)
    }
  }, [])

  const closeModal = (persistChoice = false) => {
    if (persistChoice || dontShowAgain) {
      try {
        window.localStorage.setItem(STORAGE_KEY, '1')
      } catch {
        // Ignore storage failures and still close.
      }
    }
    setIsOpen(false)
  }

  const onCtaClick = () => closeModal(dontShowAgain)

  if (!isPromo) return null

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => closeModal(dontShowAgain)}
      className="mx-auto w-full max-w-xl transform overflow-hidden rounded-2xl bg-white text-left align-middle shadow-xl transition-all"
    >
      <div className="relative overflow-hidden px-7 py-7 sm:px-8 sm:py-8">
        <div className="pointer-events-none absolute -right-12 -top-12 h-40 w-40 rounded-full bg-amber-200/60 blur-3xl" />
        <div className="pointer-events-none absolute -left-8 bottom-0 h-28 w-28 rounded-full bg-amber-100/80 blur-3xl" />
        <img
          src="/img/icons/3d/locker-sm-min.png"
          className="pointer-events-none absolute -right-3 -top-6 z-10 h-20 w-auto rotate-6 opacity-15"
          alt="locker icon 3d"
        />
        <img
          src="/img/icons/3d/dollar-coin-sm-min.png"
          className="pointer-events-none absolute -bottom-6 -left-6 z-10 h-24 w-auto -rotate-6 opacity-15"
          alt="coin icon 3d"
        />

        <div className="relative">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="rounded-2xl bg-amber-100 p-3 text-amber-800">
                <HeroIcons.ReceiptPercentIcon
                  className="h-6 w-6"
                  aria-hidden="true"
                />
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-amber-800">
                  {t('yieldBoostPopup.badge')}
                </p>
                <p className="text-xl font-semibold text-neutralBlack">
                  {t('yieldBoostPopup.title')}
                </p>
              </div>
            </div>
          </div>

          <p className="mt-4 text-sm text-gray-600">
            {t('yieldBoostPopup.subtitle')}
          </p>

          <div className="mt-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <label
              className="flex cursor-pointer items-center gap-2 text-xs text-gray-600"
              htmlFor={'hide-modal'}
            >
              <input
                id={'hide-modal'}
                type="checkbox"
                className="h-4 w-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                checked={dontShowAgain}
                onChange={(event) => setDontShowAgain(event.target.checked)}
              />
              {t('yieldBoostPopup.dontShow')}
            </label>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => closeModal(dontShowAgain)}
                className="rounded-full px-3 py-2 text-xs font-semibold text-gray-600 transition hover:bg-gray-100"
              >
                {t('yieldBoostPopup.later')}
              </button>
              <Link
                href={{
                  pathname: routes.property.replace(
                    '[propertyId]',
                    PROMO_PROPERTY_ID,
                  ),
                }}
                onClick={onCtaClick}
                className="inline-flex items-center justify-center rounded-full bg-neutralBlack px-5 py-2.5 text-xs font-semibold text-white transition hover:bg-neutralBlack/90"
              >
                {t('yieldBoostPopup.cta')}
              </Link>
            </div>
          </div>

          <p className="mt-4 text-[10px] text-gray-400">
            {t('yieldBoostPopup.disclaimer')}
          </p>
        </div>
      </div>
    </Modal>
  )
}
